//angular library
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';

//angular material
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatInputModule } from '@angular/material/input';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatGridListModule } from '@angular/material/grid-list';
import {MatIconModule} from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { TableModule } from 'primeng/table';
import { MatSortModule } from '@angular/material/sort';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';

// OpenVidu
import { OpenViduComponentsModule, OpenViduComponentsConfig } from 'openvidu-components-angular';

//third party
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MsalModule, MsalRedirectComponent, MsalGuard, MsalInterceptor } from '@azure/msal-angular';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
//primeng modules
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeModule } from 'primeng/tree';
import { InputMaskModule } from 'primeng/inputmask';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { ChipsModule } from "primeng/chips";
import { AutoCompleteModule } from 'primeng/autocomplete';
//component & service & etc
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { NotRegisteredComponent } from './not-registered/not-registered.component';
import { CompaniesComponent } from './dashboard/companies/companies.component';
import { VesselsComponent } from './dashboard/vessels/vessels.component';
import { UserComponent } from './dashboard/user/user.component';
import { AdminGuard } from './_guard/admin.guard';
import { CompanyDetailComponent } from './dashboard/companies/company-detail/company-detail.component';
import { CompanyRegisterComponent } from './dashboard/companies/company-register/company-register.component';
import { CompanyListComponent } from './dashboard/companies/company-list/company-list.component';
import { UserListComponent } from './dashboard/user/user-list/user-list.component';
import { UserPreviewComponent } from './dashboard/user/user-preview/user-preview.component';
import { UserRegisterComponent } from './dashboard/user/user-register/user-register.component';
import { UserEditComponent } from './dashboard/user/user-edit/user-edit.component';
import { IportalHomeComponent } from './iportal-home/iportal-home.component';
import { environment } from 'src/environments/environment';
import { VesselListComponent } from './dashboard/vessels/vessel-list/vessel-list.component';
import { VesselDetailComponent } from './dashboard/vessels/vessel-detail/vessel-detail.component';
import { VesselRegisterComponent } from './dashboard/vessels/vessel-register/vessel-register.component';
import { VesselApplicationRegisterComponent } from './dashboard/vessels/vessel-application-register/vessel-application-register.component';
import { CompanyEditComponent } from './dashboard/companies/company-edit/company-edit.component';
import { VesselEditComponent } from './dashboard/vessels/vessel-edit/vessel-edit.component';
import { TenantsComponent } from './dashboard/tenants/tenants.component';
import { TenantListComponent } from './dashboard/tenants/tenant-list/tenant-list.component';
import { TenantRegisterComponent } from './dashboard/tenants/tenant-register/tenant-register.component';
import { TenantEditComponent } from './dashboard/tenants/tenant-edit/tenant-edit.component';
import { RbacEditComponent } from './dashboard/tenants/rbac-edit/rbac-edit.component';
import { TenantPopupComponent } from './dashboard/user/tenant-popup/tenant-popup.component';
import { TenantSelectionComponent } from './dashboard/user/tenant-selection/tenant-selection.component';
import { MeetingsComponent } from './dashboard/meetings/meetings.component';
import { RoomComponent } from './dashboard/meetings/room/room.component';
import { JoinComponent } from './dashboard/meetings/join/join.component';
import { ClaimCenterComponent } from './dashboard/claim-center/claim-center.component';
import { CalendarComponent } from './dashboard/meetings/calendar/calendar/calendar.component';
import { FullCalendarModule } from '@fullcalendar/angular';
import { EventDialogComponent } from './dashboard/meetings/event-dialog/event-dialog.component';
import { ClaimEditComponent } from './dashboard/claim-center/claim-edit/claim-edit.component';
import { ClaimNewComponent } from './dashboard/claim-center/claim-new/claim-new.component';
import { OriginalFormComponent } from './dashboard/claim-center/original-form/original-form.component';
import { ConfirmDialogComponent } from './dashboard/claim-center/claim-edit/confirm-dialog/confirm-dialog.component';
import { FileUploadComponent } from './dashboard/claim-center/file-upload/file-upload.component';
import { InquiryCenterComponent } from './dashboard/inquiry-center/inquiry-center.component';
import { InquiryEditComponent } from './dashboard/inquiry-center/inquiry-edit/inquiry-edit.component';



const config: OpenViduComponentsConfig = {
  production: environment.production,
};

export function CreateTranslateFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const isIE = window.navigator.userAgent.indexOf('MSIE') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;
const protectedResourceMap = new Map<string, Array<string>>();
const conf = environment.msalResourceMap;
conf.forEach(e => {
  protectedResourceMap.set(e.resourceURL, e.scopes);
});

@NgModule({ declarations: 
    [
        AppComponent,
        //LoginComponent,
        IportalHomeComponent,
        //ProfileComponent,
        DashboardComponent,
        NotRegisteredComponent,
        CompaniesComponent,
        VesselsComponent,
        UserComponent,
        CompanyDetailComponent,
        TenantsComponent,
        TenantListComponent,
        TenantRegisterComponent,
        TenantEditComponent,
        RbacEditComponent,
        CompanyDetailComponent,
        CompanyRegisterComponent,
        CompanyListComponent,
        UserListComponent,
        UserPreviewComponent,
        UserRegisterComponent,
        UserEditComponent,
        TenantPopupComponent,
        TenantSelectionComponent,
        VesselListComponent,
        VesselDetailComponent,
        VesselRegisterComponent,
        VesselApplicationRegisterComponent,
        CompanyEditComponent,
        VesselEditComponent,
        MeetingsComponent,
        RoomComponent,
        JoinComponent,
        ClaimCenterComponent,
        CalendarComponent,
        EventDialogComponent,
        ClaimEditComponent,
        ClaimNewComponent,
        OriginalFormComponent,
        ConfirmDialogComponent,
        FileUploadComponent,
        InquiryCenterComponent,
        InquiryEditComponent,
    ],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        MatButtonModule,
        MatToolbarModule,
        MatTooltipModule,
        MatListModule,
        MatFormFieldModule,
        MatSelectModule,
        MatChipsModule,
        MatInputModule,
        ReactiveFormsModule,
        MatTableModule,
        ScrollingModule,
        MatSidenavModule,
        MatAutocompleteModule,
        FormsModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        MatExpansionModule,
        MatCardModule,
        MatDatepickerModule,
        MatButtonModule,
        MatFormFieldModule,
        MatNativeDateModule,
        MatGridListModule,
        MatIconModule,
        MatPaginatorModule,
        TableModule,
        TreeSelectModule,
        TreeModule,
        InputMaskModule,
        MatSortModule,
        DialogModule,
        MatStepperModule,
        MatProgressBarModule,
        MatTabsModule,
        MatButtonToggleModule,
        InputTextModule,
        CheckboxModule,
        DropdownModule,
        CalendarModule,
        ChipsModule,
        AutoCompleteModule,
        BaseChartDirective,
        FullCalendarModule,
        CdkVirtualScrollViewport,
        OpenViduComponentsModule.forRoot(config),
        // MatMomentDateModule,
        MsalModule.forRoot(new PublicClientApplication({
            auth: {
                clientId: 'b1e3fa6b-9104-4275-ab2d-8342585f02c6',
                authority: 'https://login.microsoftonline.com/kikuho.onmicrosoft.com',
                redirectUri: environment.msalConfig.auth.redirectUri
            },
            cache: {
                cacheLocation: 'localStorage',
                storeAuthStateInCookie: isIE,
            }
        }), {
            interactionType: InteractionType.Redirect
        }, {
            interactionType: InteractionType.Redirect,
            protectedResourceMap: new Map([
                [environment.msalResourceMap[0].resourceURL, environment.msalResourceMap[0].scopes]
            ])
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (CreateTranslateFactory),
                deps: [HttpClient]
            },
            defaultLanguage: 'en'
        })], providers: [
        MsalGuard,
        AdminGuard,
        provideCharts(withDefaultRegisterables()),
        { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
}
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}